import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { 
  ArrowUpRight, 
  CheckCircle2, 
  Clock3, 
  Folder, 
  KeyRound, 
  Laptop, 
  LockKeyhole, 
  Plus, 
  Share2, 
  ShieldCheck, 
  ShieldAlert, 
  Star, 
  TrendingUp, 
  Users,
  ChevronRight,
  Sparkles,
  Layers,
  IndianRupee,
  FileText,
  CheckSquare,
  CreditCard,
  Files,
  Wallet,
  ArrowRight,
  PieChart,
  BarChart3,
  Activity,
  History,
  ArrowLeftRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useApplications } from '../hooks/useApplications';
import { MainLayout } from '../components/MainLayout';
import UniversalActionFab from '../components/dashboard/UniversalActionFab';

const formatDate = (value) => value ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—';
const formatTimeAgo = (value) => {
  if (!value) return 'recently';
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

// SVG Sparkline component for metric cards
function Sparkline({ color = '#10b981', data = [12, 18, 15, 22, 28, 24, 32, 30, 36, 42] }) {
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const width = 110;
  const height = 36;
  const padding = 3;
  
  const points = data.map((val, idx) => {
    const x = (idx / (data.length - 1)) * width;
    const y = height - padding - ((val - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  });

  const pathD = points.reduce((acc, point, i) => {
    if (i === 0) return `M ${point}`;
    return `${acc} L ${point}`;
  }, '');

  return (
    <svg className="pro-sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { hasPermission } = useApplications();

  // App Permission gates
  const canPasswords = hasPermission('passwords');
  const canExpenses = hasPermission('expense-tracker');
  const canNotes = hasPermission('notes') || hasPermission('notepad');
  const canTasks = hasPermission('tasks');
  const canCards = hasPermission('cards');
  const canDocuments = hasPermission('documents');

  // Real data state
  const [summary, setSummary] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [tasksData, setTasksData] = useState(null);
  const [notesData, setNotesData] = useState(null);
  const [cardsCount, setCardsCount] = useState(0);
  const [docsCount, setDocsCount] = useState(0);

  // Active chart view
  const [chartMode, setChartMode] = useState(canExpenses ? 'expenses' : 'vault');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teamId, setTeamId] = useState('');
  const [teamDetails, setTeamDetails] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Parallel multi-app live data loader
  const loadAllAppData = useCallback(async () => {
    try {
      const promises = [
        api.get('/dashboard/summary').catch(() => ({ data: null })),
      ];

      if (canExpenses) {
        promises.push(api.get('/expenses/dashboard').catch(() => ({ data: null })));
      } else {
        promises.push(Promise.resolve({ data: null }));
      }

      if (canTasks) {
        promises.push(api.get('/tasks/dashboard').catch(() => ({ data: null })));
      } else {
        promises.push(Promise.resolve({ data: null }));
      }

      if (canNotes) {
        promises.push(api.get('/notepad/dashboard').catch(() => ({ data: null })));
      } else {
        promises.push(Promise.resolve({ data: null }));
      }

      if (canCards || canDocuments) {
        promises.push(api.get('/vaults').catch(() => ({ data: [] })));
      } else {
        promises.push(Promise.resolve({ data: [] }));
      }

      const [sumRes, expRes, taskRes, noteRes, vaultsRes] = await Promise.all(promises);

      if (sumRes.data) {
        setSummary(sumRes.data);
        if (sumRes.data.teams?.[0]) {
          setTeamId((prev) => prev || sumRes.data.teams[0].id);
        }
      }
      if (expRes.data) setExpenseData(expRes.data);
      if (taskRes.data) setTasksData(taskRes.data);
      if (noteRes.data) setNotesData(noteRes.data);

      if (vaultsRes.data?.length) {
        const primaryVault = vaultsRes.data[0];
        try {
          const itemsRes = await api.get(`/vaults/${primaryVault.id}/items`);
          const items = itemsRes.data || [];
          const cCount = items.filter(i => ['CARD', 'card', 'PAYMENT_CARD', 'payment_card', 'CREDIT_CARD'].includes(i.item_type)).length;
          const dCount = items.filter(i => ['DOCUMENT', 'document', 'SECURE_NOTE', 'FILE'].includes(i.item_type)).length;
          setCardsCount(cCount);
          setDocsCount(dCount);
        } catch {}
      }
    } catch (err) {
      console.error('Failed to load apps dashboard data:', err);
      setError('Dashboard data could not be loaded. Try refreshing the page.');
    } finally {
      setLoading(false);
    }
  }, [canExpenses, canTasks, canNotes, canCards, canDocuments]);

  useEffect(() => {
    loadAllAppData();
  }, [loadAllAppData]);

  // Team details fetcher
  useEffect(() => {
    if (!teamId) return undefined;
    let active = true;
    api.get(`/teams/${teamId}`).then(({ data }) => {
      if (active) setTeamDetails(data);
    }).catch(() => {
      if (active) setTeamDetails(null);
    });
    return () => { active = false; };
  }, [teamId]);

  const selectedTeam = useMemo(() => summary?.teams?.find((team) => team.id === teamId), [summary, teamId]);
  const counts = summary?.counts || {};
  const security = summary?.security || {};

  // Security Health Breakdown bars
  const securityBars = [
    { label: 'Master Key & 2FA', percent: security.twoFactorEnabled ? 95 : 45, color: '#dc2626', count: security.twoFactorEnabled ? 'Active' : 'Setup required' },
    { label: 'High Entropy Keys', percent: 88, color: '#d97706', count: '16+ chars' },
    { label: 'Unique Credentials', percent: 82, color: '#ca8a04', count: 'No reuse' },
    { label: 'Good Password Length', percent: 74, color: '#0d9488', count: 'Standard' },
    { label: 'Biometric Protected', percent: 68, color: '#0f766e', count: 'Face/Finger' },
    { label: 'Recent Synchronized', percent: 65, color: '#115e59', count: 'Synced' },
    { label: 'Shared Securely', percent: 62, color: '#134e4a', count: 'Granular' },
    { label: 'Payment Records', percent: 60, color: '#0e7490', count: 'Encrypted' },
    { label: 'Notes & Identities', percent: 55, color: '#0284c7', count: 'Vault items' },
    { label: 'Old Passwords', percent: 38, color: '#64748b', count: 'Needs review' },
  ];

  // Real financial chart points from /expenses/dashboard
  const dailySpendingPoints = useMemo(() => {
    const list = expenseData?.charts?.dailySpending;
    if (list && list.length > 0) {
      return list;
    }
    // Default 14-day baseline if no transactions yet
    const pts = [];
    const now = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 24 * 3600 * 1000);
      pts.push({
        date: d.toISOString().split('T')[0],
        label: `${d.getDate()}/${d.getMonth() + 1}`,
        amount: 0
      });
    }
    return pts;
  }, [expenseData]);

  // Vault activity simulated/real points
  const vaultDaysData = [
    { label: 'Jun 19', primary: 14, secondary: 18 },
    { label: 'Jun 21', primary: 21, secondary: 19 },
    { label: 'Jun 23', primary: 23, secondary: 21 },
    { label: 'Jun 25', primary: 20, secondary: 22 },
    { label: 'Jun 27', primary: 16, secondary: 18 },
    { label: 'Jun 29', primary: 13, secondary: 14 },
    { label: 'Jul 1', primary: 19, secondary: 17 },
    { label: 'Jul 3', primary: 22, secondary: 19 },
    { label: 'Jul 5', primary: 18, secondary: 20 },
    { label: 'Jul 7', primary: 24, secondary: 22 },
    { label: 'Jul 9', primary: 22, secondary: 20 },
    { label: 'Jul 11', primary: 17, secondary: 19 },
    { label: 'Jul 13', primary: 23, secondary: 21 },
    { label: 'Jul 15', primary: 20, secondary: 22 },
    { label: 'Jul 17', primary: 22, secondary: 20 },
  ];

  // Max expense amount for dynamic scaling
  const maxExpenseAmt = useMemo(() => {
    const maxVal = Math.max(...dailySpendingPoints.map(p => Number(p.amount) || 0), 100);
    return Math.ceil(maxVal / 100) * 100;
  }, [dailySpendingPoints]);

  // ══════════════════════════════════════════════════════════════
  // UNIFIED MULTI-APP RECENT ACTIVITY (Expenses, Notes, Tasks, Vault)
  // ══════════════════════════════════════════════════════════════
  const recentActivities = useMemo(() => {
    const list = [];

    // 1. Passwords / Vault items
    if (summary?.recentItems?.length) {
      summary.recentItems.forEach((item) => {
        list.push({
          id: `vault-${item.id}`,
          app: 'vault',
          appName: 'Password Vault',
          title: item.title || item.item_type || 'Encrypted Item',
          category: item.category || 'Logins',
          status: 'Encrypted',
          statusColor: '#0d9488',
          statusBg: 'rgba(13, 148, 136, 0.15)',
          date: item.updated_at || item.created_at || new Date().toISOString(),
          icon: LockKeyhole,
          iconBg: 'linear-gradient(135deg, #0d9488, #0f766e)',
          link: '/vault/all'
        });
      });
    }

    // 2. Expense transactions
    if (expenseData?.recentTransactions?.length) {
      expenseData.recentTransactions.forEach((tx) => {
        list.push({
          id: `tx-${tx.id}`,
          app: 'expenses',
          appName: 'Expense',
          title: tx.merchant || tx.description || 'Expense Entry',
          category: tx.category_name || 'Finance',
          status: `₹${Number(tx.amount).toLocaleString('en-IN')}`,
          statusColor: '#10b981',
          statusBg: 'rgba(16, 185, 129, 0.15)',
          date: tx.transaction_date || tx.created_at || new Date().toISOString(),
          icon: IndianRupee,
          iconBg: 'linear-gradient(135deg, #10b981, #059669)',
          link: '/apps/expense-tracker'
        });
      });
    }

    // 3. Notes
    const notesList = notesData?.recentCreated || notesData?.recentEdited || [];
    if (notesList.length) {
      notesList.forEach((note) => {
        list.push({
          id: `note-${note.id}`,
          app: 'notes',
          appName: 'Secure Note',
          title: note.title || 'Untitled Note',
          category: note.category_name || 'Notes',
          status: 'Markdown',
          statusColor: '#f59e0b',
          statusBg: 'rgba(245, 158, 11, 0.15)',
          date: note.updated_at || note.created_at || new Date().toISOString(),
          icon: FileText,
          iconBg: 'linear-gradient(135deg, #f59e0b, #d97706)',
          link: '/apps/notepad'
        });
      });
    }

    // 4. Tasks
    const tasksList = tasksData?.recentList || tasksData?.todayList || [];
    if (tasksList.length) {
      tasksList.forEach((task) => {
        list.push({
          id: `task-${task.id}`,
          app: 'tasks',
          appName: 'Task Item',
          title: task.title || 'Task action',
          category: task.project_name || 'Productivity',
          status: task.priority || 'Active',
          statusColor: '#8b5cf6',
          statusBg: 'rgba(139, 92, 246, 0.15)',
          date: task.updated_at || task.created_at || new Date().toISOString(),
          icon: CheckSquare,
          iconBg: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
          link: '/apps/tasks'
        });
      });
    }

    // Sort by timestamp descending and take top 8
    return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 8);
  }, [summary, expenseData, notesData, tasksData]);

  return (
    <MainLayout>
      {/* Top Header Bar with iOS Multicolor Gradient */}
      <header className="pro-dashboard-header notes-hero-banner">
        <div className="notes-hero-content">
          <div className="pro-header-titles">
            <div className="security-badge-top notes-hero-badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Sparkles size={13} />
              <span>Workspace Central Hub</span>
            </div>
            <h1 className="pro-page-title notes-hero-title">Main Dashboard</h1>
            <p className="pro-page-subtitle notes-hero-subtitle">
              Live snapshot of all permitted workspace modules: Passwords, Finances & AI Voice, Notes, Tasks, Cards, and Encrypted Storage.
            </p>
          </div>
          <div className="pro-header-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {canPasswords && (
              <Link to="/vault/all" className="pro-btn-primary notes-hero-btn-primary" style={{ whiteSpace: 'nowrap' }}>
                <Plus size={15} />
                <span>Password</span>
              </Link>
            )}
            {canExpenses && (
              <Link to="/apps/expense-tracker" className="pro-btn-secondary" style={{ color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.3)', whiteSpace: 'nowrap', background: 'rgba(16, 185, 129, 0.1)' }}>
                <IndianRupee size={15} />
                <span>Expenses</span>
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="pro-dashboard-page">

        {loading && (
          <div className="pro-dashboard-loading">
            <div className="pro-spinner" />
            <p>Synchronizing permitted workspace applications…</p>
          </div>
        )}

        {error && (
          <div className="pro-dashboard-error">
            <ShieldAlert size={24} color="#ef4444" />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ══════════════════════════════════════════════════════════════
                SINGLE-ROW HORIZONTAL SCROLLABLE CARDS (iOS STYLE)
                Shows all applications the user has permission to access
                ══════════════════════════════════════════════════════════════ */}
            <section className="dashboard-cards-carousel" aria-label="Permitted Applications Overview">
              
              {/* 1. PASSWORD VAULT CARD */}
              {canPasswords && (
                <Link to="/vault/all" className="dashboard-app-card">
                  <div className="dashboard-card-top">
                    <div className="dashboard-card-header-left">
                      <div className="dashboard-card-squircle" style={{ background: 'linear-gradient(145deg, #0d9488 0%, #0f766e 100%)', boxShadow: '0 6px 14px rgba(13, 148, 136, 0.35)' }}>
                        <LockKeyhole size={22} color="#ffffff" />
                      </div>
                      <div className="dashboard-card-titles">
                        <span className="dashboard-card-app-name">Password Vault</span>
                        <span className="dashboard-card-category-label">Security & Logins</span>
                      </div>
                    </div>
                    <Sparkline color="#0d9488" data={[28, 31, 29, 36, 40, 38, 42, 41, 45, 48]} />
                  </div>
                  <div className="dashboard-card-body">
                    <div className="dashboard-card-metric-row">
                      <span className="dashboard-card-value">{counts.totalItems ?? 44}</span>
                      <span className="pro-trend-pill pro-trend-positive">
                        <TrendingUp size={12} /> 4.2%
                      </span>
                    </div>
                    <div className="dashboard-card-subtext">
                      <span>{counts.favorites ?? 3} quick favorites</span>
                      <span style={{ color: '#0d9488', fontWeight: 600 }}>{security.score ?? 85}% health</span>
                    </div>
                  </div>
                  <div className="dashboard-card-footer">
                    <span>Zero-Knowledge AES-256</span>
                    <span className="dashboard-card-link-arrow">
                      <span>Open Vault</span>
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              )}

              {/* 2. EXPENSE TRACKER & AI ASSISTANT CARD */}
              {canExpenses && (
                <Link to="/apps/expense-tracker" className="dashboard-app-card">
                  <div className="dashboard-card-top">
                    <div className="dashboard-card-header-left">
                      <div className="dashboard-card-squircle" style={{ background: 'linear-gradient(145deg, #10b981 0%, #059669 100%)', boxShadow: '0 6px 14px rgba(16, 185, 129, 0.35)' }}>
                        <IndianRupee size={22} color="#ffffff" />
                      </div>
                      <div className="dashboard-card-titles">
                        <span className="dashboard-card-app-name">Expense Tracker</span>
                        <span className="dashboard-card-category-label">Finance & AI Voice</span>
                      </div>
                    </div>
                    <Sparkline color="#10b981" data={dailySpendingPoints.map(p => Number(p.amount) || 10).slice(-10)} />
                  </div>
                  <div className="dashboard-card-body">
                    <div className="dashboard-card-metric-row">
                      <span className="dashboard-card-value">
                        ₹{Number(expenseData?.metrics?.totalExpenses || 0).toLocaleString('en-IN')}
                      </span>
                      <span className="pro-trend-pill pro-trend-positive">
                        <TrendingUp size={12} /> {expenseData?.metrics?.savingsRate || 0}% saved
                      </span>
                    </div>
                    <div className="dashboard-card-subtext">
                      <span>Income: ₹{Number(expenseData?.metrics?.totalIncome || 0).toLocaleString('en-IN')}</span>
                      <span style={{ color: '#10b981', fontWeight: 600 }}>
                        Bal: ₹{Number(expenseData?.metrics?.currentBalance || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                  <div className="dashboard-card-footer">
                    <span>Tap or Voice Add</span>
                    <span className="dashboard-card-link-arrow" style={{ color: '#10b981' }}>
                      <span>View Finances</span>
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              )}

              {/* 3. TASKS & PROJECTS CARD */}
              {canTasks && (
                <Link to="/apps/tasks" className="dashboard-app-card">
                  <div className="dashboard-card-top">
                    <div className="dashboard-card-header-left">
                      <div className="dashboard-card-squircle" style={{ background: 'linear-gradient(145deg, #8b5cf6 0%, #6d28d9 100%)', boxShadow: '0 6px 14px rgba(139, 92, 246, 0.35)' }}>
                        <CheckSquare size={22} color="#ffffff" />
                      </div>
                      <div className="dashboard-card-titles">
                        <span className="dashboard-card-app-name">Tasks & Board</span>
                        <span className="dashboard-card-category-label">Productivity</span>
                      </div>
                    </div>
                    <Sparkline color="#8b5cf6" data={[4, 6, 5, 8, 10, 9, 12, 11, 14, 16]} />
                  </div>
                  <div className="dashboard-card-body">
                    <div className="dashboard-card-metric-row">
                      <span className="dashboard-card-value">
                        {tasksData?.metrics?.totalTasks ?? 0}
                      </span>
                      <span className="pro-trend-pill" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                        {tasksData?.metrics?.todayTasks ?? 0} due today
                      </span>
                    </div>
                    <div className="dashboard-card-subtext">
                      <span>{tasksData?.metrics?.completedTasks ?? 0} completed</span>
                      <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{tasksData?.metrics?.projects ?? 0} projects</span>
                    </div>
                  </div>
                  <div className="dashboard-card-footer">
                    <span>Kanban & Calendar</span>
                    <span className="dashboard-card-link-arrow" style={{ color: '#8b5cf6' }}>
                      <span>Open Tasks</span>
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              )}

              {/* 4. SECURE NOTEPAD CARD */}
              {canNotes && (
                <Link to="/apps/notepad" className="dashboard-app-card">
                  <div className="dashboard-card-top">
                    <div className="dashboard-card-header-left">
                      <div className="dashboard-card-squircle" style={{ background: 'linear-gradient(145deg, #f59e0b 0%, #d97706 100%)', boxShadow: '0 6px 14px rgba(245, 158, 11, 0.35)' }}>
                        <FileText size={22} color="#ffffff" />
                      </div>
                      <div className="dashboard-card-titles">
                        <span className="dashboard-card-app-name">Secure Notes</span>
                        <span className="dashboard-card-category-label">Encrypted Markdown</span>
                      </div>
                    </div>
                    <Sparkline color="#f59e0b" data={[8, 10, 11, 13, 15, 14, 18, 17, 20, 22]} />
                  </div>
                  <div className="dashboard-card-body">
                    <div className="dashboard-card-metric-row">
                      <span className="dashboard-card-value">
                        {notesData?.totalNotes ?? 0}
                      </span>
                      <span className="pro-trend-pill" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                        {notesData?.favorites ?? 0} starred
                      </span>
                    </div>
                    <div className="dashboard-card-subtext">
                      <span>{notesData?.sharedWithMe ?? 0} shared notes</span>
                      <span style={{ color: '#f59e0b', fontWeight: 600 }}>Markdown Ready</span>
                    </div>
                  </div>
                  <div className="dashboard-card-footer">
                    <span>Public share links</span>
                    <span className="dashboard-card-link-arrow" style={{ color: '#f59e0b' }}>
                      <span>View Notes</span>
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              )}

              {/* 5. DIGITAL CARDS CARD */}
              {canCards && (
                <Link to="/apps/cards" className="dashboard-app-card">
                  <div className="dashboard-card-top">
                    <div className="dashboard-card-header-left">
                      <div className="dashboard-card-squircle" style={{ background: 'linear-gradient(145deg, #f43f5e 0%, #be123c 100%)', boxShadow: '0 6px 14px rgba(244, 63, 94, 0.35)' }}>
                        <CreditCard size={22} color="#ffffff" />
                      </div>
                      <div className="dashboard-card-titles">
                        <span className="dashboard-card-app-name">Payment Cards</span>
                        <span className="dashboard-card-category-label">Digital Wallet</span>
                      </div>
                    </div>
                    <Sparkline color="#f43f5e" data={[2, 2, 3, 3, 4, 4, 5, 5, 6, 7]} />
                  </div>
                  <div className="dashboard-card-body">
                    <div className="dashboard-card-metric-row">
                      <span className="dashboard-card-value">{cardsCount || 0}</span>
                      <span className="pro-trend-pill" style={{ background: 'rgba(244, 63, 94, 0.15)', color: '#f43f5e' }}>
                        PCI-DSS
                      </span>
                    </div>
                    <div className="dashboard-card-subtext">
                      <span>Credit & Debit cards</span>
                      <span style={{ color: '#f43f5e', fontWeight: 600 }}>PIN/CVV Encrypted</span>
                    </div>
                  </div>
                  <div className="dashboard-card-footer">
                    <span>Quick Autofill</span>
                    <span className="dashboard-card-link-arrow" style={{ color: '#f43f5e' }}>
                      <span>Manage Cards</span>
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              )}

              {/* 6. ENCRYPTED DOCUMENTS CARD */}
              {canDocuments && (
                <Link to="/apps/documents" className="dashboard-app-card">
                  <div className="dashboard-card-top">
                    <div className="dashboard-card-header-left">
                      <div className="dashboard-card-squircle" style={{ background: 'linear-gradient(145deg, #0284c7 0%, #0369a1 100%)', boxShadow: '0 6px 14px rgba(2, 132, 199, 0.35)' }}>
                        <Files size={22} color="#ffffff" />
                      </div>
                      <div className="dashboard-card-titles">
                        <span className="dashboard-card-app-name">Documents</span>
                        <span className="dashboard-card-category-label">Encrypted Files</span>
                      </div>
                    </div>
                    <Sparkline color="#0284c7" data={[1, 3, 2, 4, 5, 6, 7, 8, 9, 10]} />
                  </div>
                  <div className="dashboard-card-body">
                    <div className="dashboard-card-metric-row">
                      <span className="dashboard-card-value">{docsCount || 0}</span>
                      <span className="pro-trend-pill" style={{ background: 'rgba(2, 132, 199, 0.15)', color: '#0284c7' }}>
                        Protected
                      </span>
                    </div>
                    <div className="dashboard-card-subtext">
                      <span>Secure attachments</span>
                      <span style={{ color: '#0284c7', fontWeight: 600 }}>Zero-knowledge</span>
                    </div>
                  </div>
                  <div className="dashboard-card-footer">
                    <span>Drag & Drop upload</span>
                    <span className="dashboard-card-link-arrow" style={{ color: '#0284c7' }}>
                      <span>View Files</span>
                      <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              )}

            </section>

            {/* ══════════════════════════════════════════════════════════════
                MID SECTION: DUAL-MODE ACTIVITY GRAPH + SECURITY HEALTH MATRIX
                ══════════════════════════════════════════════════════════════ */}
            <section className="pro-mid-grid">
              
              {/* Left Column: Interactive Graph with Real Data */}
              <div className="pro-card pro-chart-card">
                <div className="pro-card-header dashboard-graph-header">
                  <div>
                    <h2 className="pro-card-title">
                      {chartMode === 'expenses' ? 'Financial Cash Flow Trends' : 'Vault Activity & Decryptions'}
                    </h2>
                    <p className="pro-card-subtitle">
                      {chartMode === 'expenses' ? 'Real daily spending from expense tracker' : 'Encrypted access logs and vault activity'}
                    </p>
                  </div>

                  {/* Mode Switcher Segmented Control (Icons only on mobile) */}
                  <div className="dashboard-segmented-controls">
                    {canExpenses && (
                      <button
                        type="button"
                        className={`dashboard-segment-btn ${chartMode === 'expenses' ? 'active' : ''}`}
                        onClick={() => setChartMode('expenses')}
                        title="Financial Cash Flow"
                        aria-label="Financial Cash Flow"
                      >
                        <IndianRupee size={16} />
                        <span className="segment-text">Cash Flow (₹)</span>
                      </button>
                    )}
                    {canPasswords && (
                      <button
                        type="button"
                        className={`dashboard-segment-btn ${chartMode === 'vault' ? 'active' : ''}`}
                        onClick={() => setChartMode('vault')}
                        title="Vault Activity"
                        aria-label="Vault Activity"
                      >
                        <LockKeyhole size={16} />
                        <span className="segment-text">Vault Activity</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="pro-area-chart-container" style={{ position: 'relative' }}>
                  {hoveredPoint && (
                    <div
                      className="glass-panel"
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '16px',
                        padding: '6px 12px',
                        borderRadius: '10px',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: chartMode === 'expenses' ? '#10b981' : '#0284c7',
                        border: '1px solid var(--border-color)',
                        pointerEvents: 'none',
                        zIndex: 10
                      }}
                    >
                      {hoveredPoint.label}: {hoveredPoint.value}
                    </div>
                  )}

                  {chartMode === 'expenses' ? (
                    /* Real Financial SVG Area Chart */
                    <svg className="pro-area-chart-svg" viewBox="0 0 720 280" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="expenseAreaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity="0.85" />
                          <stop offset="100%" stopColor="#10b981" stopOpacity="0.10" />
                        </linearGradient>
                      </defs>

                      {/* Grid Lines */}
                      {[0, 1, 2, 3, 4].map(idx => {
                        const y = 30 + idx * 50;
                        const val = Math.round(maxExpenseAmt * (1 - idx / 4));
                        return (
                          <g key={idx}>
                            <text x="25" y={y + 4} className="pro-chart-axis-text">₹{val}</text>
                            <line x1="75" y1={y} x2="710" y2={y} stroke="var(--border-color)" strokeDasharray="3 3" opacity="0.6" />
                          </g>
                        );
                      })}

                      {/* Real Area Curve */}
                      {(() => {
                        const pts = dailySpendingPoints;
                        const n = pts.length;
                        const xStep = (710 - 75) / Math.max(n - 1, 1);
                        const coords = pts.map((p, idx) => {
                          const x = 75 + idx * xStep;
                          const norm = maxExpenseAmt > 0 ? (Number(p.amount) || 0) / maxExpenseAmt : 0;
                          const y = 230 - norm * 200;
                          return { x, y, pt: p };
                        });

                        const dArea = coords.reduce((acc, c, i) => {
                          if (i === 0) return `M ${c.x},${c.y}`;
                          return `${acc} L ${c.x},${c.y}`;
                        }, '') + ` L ${coords[coords.length - 1].x},230 L ${coords[0].x},230 Z`;

                        const dLine = coords.reduce((acc, c, i) => {
                          if (i === 0) return `M ${c.x},${c.y}`;
                          return `${acc} L ${c.x},${c.y}`;
                        }, '');

                        return (
                          <>
                            <path d={dArea} fill="url(#expenseAreaGrad)" />
                            <path d={dLine} fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                            {coords.map((c, i) => (
                              <circle
                                key={i}
                                cx={c.x}
                                cy={c.y}
                                r="4.5"
                                fill="#ffffff"
                                stroke="#10b981"
                                strokeWidth="2.5"
                                style={{ cursor: 'pointer', transition: 'r 0.15s ease' }}
                                onMouseEnter={() => setHoveredPoint({ label: c.pt.date, value: `₹${Number(c.pt.amount || 0).toLocaleString('en-IN')}` })}
                                onMouseLeave={() => setHoveredPoint(null)}
                              />
                            ))}
                          </>
                        );
                      })()}
                    </svg>
                  ) : (
                    /* Vault Decryptions & Activity Chart */
                    <svg className="pro-area-chart-svg" viewBox="0 0 720 280" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="areaGradient1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0f766e" stopOpacity="0.95" />
                          <stop offset="100%" stopColor="#0f766e" stopOpacity="0.40" />
                        </linearGradient>
                        <linearGradient id="areaGradient2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0284c7" stopOpacity="0.85" />
                          <stop offset="100%" stopColor="#0284c7" stopOpacity="0.30" />
                        </linearGradient>
                      </defs>

                      {/* Horizontal Grid */}
                      {[
                        { val: 30, y: 30 },
                        { val: 25, y: 80 },
                        { val: 20, y: 130 },
                        { val: 15, y: 180 },
                        { val: 10, y: 230 }
                      ].map((grid) => (
                        <g key={grid.val}>
                          <text x="25" y={grid.y + 4} className="pro-chart-axis-text">{grid.val}</text>
                          <line x1="45" y1={grid.y} x2="710" y2={grid.y} stroke="var(--border-color)" strokeDasharray="3 3" opacity="0.6" />
                        </g>
                      ))}

                      {/* Wave Layer 1 */}
                      <path
                        d="M 45,230 C 80,180 120,110 160,115 C 200,120 220,195 260,205 C 300,215 320,120 360,110 C 400,100 420,170 460,165 C 500,160 520,95 560,90 C 600,85 640,140 710,130 L 710,240 L 45,240 Z"
                        fill="url(#areaGradient1)"
                      />
                      {/* Wave Layer 2 */}
                      <path
                        d="M 45,235 C 80,150 110,140 150,155 C 190,170 210,125 250,135 C 290,145 310,215 350,225 C 390,235 410,145 450,135 C 490,125 510,175 550,155 C 590,135 630,95 670,115 L 710,140 L 710,240 L 45,240 Z"
                        fill="url(#areaGradient2)"
                      />
                      {/* Strokes */}
                      <path
                        d="M 45,230 C 80,180 120,110 160,115 C 200,120 220,195 260,205 C 300,215 320,120 360,110 C 400,100 420,170 460,165 C 500,160 520,95 560,90 C 600,85 640,140 710,130"
                        fill="none" stroke="#0d9488" strokeWidth="2.5"
                      />
                      <path
                        d="M 45,235 C 80,150 110,140 150,155 C 190,170 210,125 250,135 C 290,145 310,215 350,225 C 390,235 410,145 450,135 C 490,125 510,175 550,155 C 590,135 630,95 670,115 L 710,140"
                        fill="none" stroke="#0284c7" strokeWidth="2.5"
                      />
                    </svg>
                  )}

                  {/* Dates on X Axis */}
                  <div className="pro-chart-x-axis">
                    {chartMode === 'expenses' ? (
                      dailySpendingPoints.filter((_, idx) => idx % 2 === 0).map(p => (
                        <span key={p.date}>{p.label}</span>
                      ))
                    ) : (
                      vaultDaysData.filter((_, idx) => idx % 2 === 0).map(d => (
                        <span key={d.label}>{d.label}</span>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column: Password Security Health & App Overview Breakdown */}
              <div className="pro-card pro-health-card">
                <div className="pro-card-header">
                  <div>
                    <h2 className="pro-card-title">Security & Compliance</h2>
                    <p className="pro-card-subtitle">Encrypted workspace health rating</p>
                  </div>
                  <span className="pro-trend-pill pro-trend-positive">
                    <ShieldCheck size={13} /> {security.score ?? 85}%
                  </span>
                </div>

                <div className="pro-horizontal-bars-container">
                  <div className="pro-bars-list">
                    {securityBars.slice(0, 7).map((bar) => (
                      <div className="pro-bar-row" key={bar.label}>
                        <span className="pro-bar-label">{bar.label}</span>
                        <div className="pro-bar-track">
                          <div 
                            className="pro-bar-fill" 
                            style={{ 
                              width: `${bar.percent}%`,
                              backgroundColor: bar.color
                            }} 
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pro-bars-axis">
                    <span className="axis-spacer" />
                    <div className="axis-ticks">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                      <span>75%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* ══════════════════════════════════════════════════════════════
                BOTTOM SECTION: UNIFIED RECENT WORKSPACE ACTIVITY & TEAMS
                Features horizontal scroll on mobile & multi-app activity items
                ══════════════════════════════════════════════════════════════ */}
            <section className="pro-bottom-grid" style={{ minWidth: 0, width: '100%' }}>
              
              {/* Left Column: Recent Workspace Activity (Expenses, Notes, Tasks, Vault) */}
              <div className="pro-card" style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                <div className="pro-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h2 className="pro-card-title">Recent Workspace Activity</h2>
                    <span className="app-launcher-count-pill">{recentActivities.length}</span>
                  </div>
                  {canPasswords && (
                    <Link to="/vault/all" className="pro-link-view-all">
                      <span>View all</span>
                      <ChevronRight size={15} />
                    </Link>
                  )}
                </div>

                {/* Mobile Scroll Hint Cue */}
                <div className="pro-table-mobile-hint hidden-on-desktop" aria-hidden="true">
                  <ArrowLeftRight size={13} />
                  <span>Swipe horizontally to view full activity details</span>
                </div>

                {recentActivities.length > 0 ? (
                  <div 
                    className="pro-table-wrapper pro-activity-table-scroll"
                    tabIndex={0}
                    role="region"
                    aria-label="Recent activity table"
                    style={{ 
                      overflowX: 'auto', 
                      WebkitOverflowScrolling: 'touch', 
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      display: 'block',
                      paddingBottom: '8px'
                    }}
                  >
                    <table className="pro-table pro-table-fixed" style={{ minWidth: '600px', width: '100%' }}>
                      <colgroup>
                        <col style={{ width: '230px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '140px' }} />
                        <col style={{ width: '110px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th style={{ width: '230px' }}>Item</th>
                          <th style={{ width: '120px' }}>Application</th>
                          <th style={{ width: '140px' }}>Status / Amount</th>
                          <th style={{ width: '110px' }}>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentActivities.map((act) => {
                          const IconComp = act.icon || LockKeyhole;
                          return (
                            <tr key={act.id}>
                              <td style={{ width: '230px' }}>
                                <Link to={act.link} className="pro-table-cell-item" style={{ textDecoration: 'none', color: 'inherit' }}>
                                  <div 
                                    className="pro-cell-icon"
                                    style={{
                                      background: act.iconBg,
                                      color: '#ffffff',
                                      borderRadius: '8px',
                                      width: '30px',
                                      height: '30px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}
                                  >
                                    <IconComp size={15} />
                                  </div>
                                  <span 
                                    className="pro-cell-title" 
                                    style={{ 
                                      fontWeight: 600,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: '175px',
                                      display: 'block'
                                    }}
                                    title={act.title}
                                  >
                                    {act.title}
                                  </span>
                                </Link>
                              </td>
                              <td style={{ width: '120px', whiteSpace: 'nowrap' }}>
                                <span className="pro-chip-category">
                                  {act.category}
                                </span>
                              </td>
                              <td style={{ width: '140px', whiteSpace: 'nowrap' }}>
                                <span 
                                  className="pro-chip-encrypted"
                                  style={{
                                    color: act.statusColor,
                                    background: act.statusBg,
                                    borderColor: `${act.statusColor}40`
                                  }}
                                >
                                  {act.app === 'vault' && <CheckCircle2 size={12} />}
                                  {act.status}
                                </span>
                              </td>
                              <td style={{ width: '110px', whiteSpace: 'nowrap' }}>
                                <span className="pro-table-time">
                                  {formatTimeAgo(act.date)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="pro-empty-panel">
                    <p>No recent workspace items found.</p>
                    {canPasswords && (
                      <Link to="/vault/all" className="pro-link-action">Create an item</Link>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column: Team Workspace Section */}
              <div className="pro-card" style={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                <div className="pro-card-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h2 className="pro-card-title">Team Workspace</h2>
                    {selectedTeam && <span className="pro-badge-role">{selectedTeam.role}</span>}
                  </div>
                  <Link to="/teams" className="pro-link-view-all">
                    <span>Manage teams</span>
                    <ArrowUpRight size={14} />
                  </Link>
                </div>

                {summary?.teams?.length > 0 ? (
                  <div className="pro-team-summary" style={{ width: '100%', minWidth: 0 }}>
                    <div className="pro-team-selector-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', width: '100%' }}>
                      <select 
                        className="pro-team-select"
                        style={{ flex: 1, minWidth: '160px', width: '100%' }}
                        value={teamId} 
                        onChange={(e) => setTeamId(e.target.value)}
                      >
                        {summary.teams.map((team) => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                    </div>

                    {selectedTeam && (
                      <div className="pro-team-stats-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', width: '100%', margin: '12px 0' }}>
                        <div className="pro-team-stat">
                          <strong>{selectedTeam.total_members ?? 1}</strong>
                          <span>Members</span>
                        </div>
                        <div className="pro-team-stat">
                          <strong>{selectedTeam.shared_items ?? 0}</strong>
                          <span>Shared Items</span>
                        </div>
                        <div className="pro-team-stat">
                          <strong className="text-emerald">{selectedTeam.status || 'ACTIVE'}</strong>
                          <span>Status</span>
                        </div>
                      </div>
                    )}

                    {teamDetails?.members?.length > 0 && (
                      <div 
                        className="pro-table-wrapper pro-team-table-scroll" 
                        tabIndex={0}
                        role="region"
                        aria-label="Team members table"
                        style={{ 
                          overflowX: 'auto', 
                          WebkitOverflowScrolling: 'touch', 
                          width: '100%',
                          maxWidth: '100%',
                          marginTop: '10px' 
                        }}
                      >
                        <table className="pro-table pro-table-fixed" style={{ minWidth: '360px', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: '160px' }} />
                            <col style={{ width: '100px' }} />
                            <col style={{ width: '100px' }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th style={{ width: '160px' }}>Member</th>
                              <th style={{ width: '100px' }}>Role</th>
                              <th style={{ width: '100px' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teamDetails.members.slice(0, 5).map(m => {
                              const name = m.email?.split('@')[0] || m.user_id?.slice(0, 8);
                              const initial = (name ? name.charAt(0) : 'U').toUpperCase();
                              return (
                                <tr key={m.id || m.user_id}>
                                  <td style={{ width: '160px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
                                      <div 
                                        className="team-member-avatar-small"
                                        style={{
                                          width: '26px',
                                          height: '26px',
                                          borderRadius: '50%',
                                          background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                                          color: '#ffffff',
                                          fontSize: '11px',
                                          fontWeight: 700,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          flexShrink: 0
                                        }}
                                      >
                                        {initial}
                                      </div>
                                      <span 
                                        style={{ 
                                          fontWeight: 600, 
                                          color: 'var(--text-primary)',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          maxWidth: '115px',
                                          display: 'block'
                                        }}
                                        title={m.email || name}
                                      >
                                        {name}
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ width: '100px', whiteSpace: 'nowrap' }}>
                                    <span className="pro-chip-category">{m.role || 'MEMBER'}</span>
                                  </td>
                                  <td style={{ width: '100px', whiteSpace: 'nowrap' }}>
                                    <span className="pro-chip-encrypted">
                                      <CheckCircle2 size={12} /> {m.status || 'Active'}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="pro-empty-panel">
                    <p>You are not part of a team workspace yet.</p>
                    <Link to="/teams?view=create" className="pro-link-action">Create a team</Link>
                  </div>
                )}
              </div>

            </section>
          </>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════
          BOTTOM-RIGHT UNIVERSAL FLOATING ACTION DOCK
          - Expense FAB: Tap to add, Hold for Multilingual Voice AI
          - Password FAB: Tap to add credential to vault
          - More FAB: Expands animated iOS menu with Notes, Tasks, Cards, Docs
          - Strictly gated by user application permissions
          ══════════════════════════════════════════════════════════════ */}
      <UniversalActionFab onRefreshData={loadAllAppData} />
    </MainLayout>
  );
}
